import React from 'react';

import { metadata } from '@atlaskit/object/metadata';

import { type ObjectData, type ObjectTileData } from './types';

// example: new-feature
// should become New feature
export const getObjectName: (name: string) => string = (name: string): string => {
	let objectName = name;

	// replace any hyphens with spaces
	// and capitalise only the first letter
	if (objectName) {
		objectName = objectName.replace(/-/g, ' ');
		objectName = objectName.charAt(0).toUpperCase() + objectName.slice(1);
	}

	return objectName;
};

// use this to cache the result
let objectsPromise: Promise<Record<string, ObjectData> | undefined> | null = null;

// get object info as a Promise which returns a Record
export const getObjects: () => Promise<Record<string, ObjectData> | undefined> = () => {
	if (!objectsPromise) {
		objectsPromise = Promise.all(
			Object.entries(metadata).map(async ([name, meta]) => {
				const object = await meta.object.packageLoader();
				return { name, object: object.default, meta };
			}),
		)
			.then((newData) =>
				newData
					.map((object) => {
						return {
							[object.name]: {
								keywords: object.meta.keywords,
								component: object.object,
								componentName: object.meta.object.componentName,
								packageName: object.meta.object.package,
								objectName: getObjectName(object.name),
								color: object.meta.color,
								icon: object.meta.icon,
								iconPackage: object.meta.iconPackage,
							} as ObjectData,
						};
					})
					.reduce((acc, b) => ({ ...acc, ...b })),
			)
			.catch(() => {
				return undefined;
			});
	}

	return objectsPromise;
};

// use this to cache the result
let objectTilesPromise: Promise<Record<string, ObjectTileData> | undefined> | null = null;

// get object tile info as a Promise which returns a Record
export const getObjectTiles: () => Promise<Record<string, ObjectTileData> | undefined> = () => {
	if (!objectTilesPromise) {
		objectTilesPromise = Promise.all(
			Object.entries(metadata).map(async ([name, meta]) => {
				const objectTile = await meta.tile.packageLoader();
				return { name, objectTile: objectTile.default, meta };
			}),
		)
			.then((newData) =>
				newData
					.map((objectTile) => {
						return {
							[objectTile.name]: {
								keywords: [...objectTile.meta.keywords, 'tile'],
								component: objectTile.objectTile,
								componentName: objectTile.meta.tile.componentName,
								packageName: objectTile.meta.tile.package,
								objectName: getObjectName(objectTile.name),
								color: objectTile.meta.color,
								icon: objectTile.meta.icon,
								iconPackage: objectTile.meta.iconPackage,
							} as ObjectTileData,
						};
					})
					.reduce((acc, b) => ({ ...acc, ...b })),
			)
			.catch(() => {
				return undefined;
			});
	}

	return objectTilesPromise;
};

export const getObjectSizes: (object: ObjectData) => {
	label: string;
	element: React.JSX.Element;
	code: string;
}[] = (
	object: ObjectData,
): {
	label: string;
	element: React.JSX.Element;
	code: string;
}[] => [
	{
		label: 'Small (12px)',
		element: <object.component label="" size="small" />,
		code: `<${object.componentName} label="" size="small" />`,
	},
	{
		label: 'Medium (16px)',
		element: <object.component label="" size="medium" />,
		code: `<${object.componentName} label="" size="medium" />`,
	},
];

export const getObjectTileSizes: (
	objectTile: ObjectTileData,
	isBold?: boolean,
) => {
	label: string;
	element: React.JSX.Element;
	code: string;
}[] = (
	objectTile: ObjectTileData,
	isBold?: boolean,
): {
	label: string;
	element: React.JSX.Element;
	code: string;
}[] => [
	{
		label: 'XSmall (20px)',
		element: <objectTile.component label="" size="xsmall" isBold={isBold} />,
		code: isBold
			? `<${objectTile.componentName} label="" size="xsmall" isBold />`
			: `<${objectTile.componentName} label="" size="xsmall" />`,
	},
	{
		label: 'Small (24px)',
		element: <objectTile.component label="" size="small" isBold={isBold} />,
		code: isBold
			? `<${objectTile.componentName} label="" size="small" isBold />`
			: `<${objectTile.componentName} label="" size="small" />`,
	},
	{
		label: 'Medium (32px)',
		element: <objectTile.component label="" size="medium" isBold={isBold} />,
		code: isBold
			? `<${objectTile.componentName} label="" size="medium" isBold />`
			: `<${objectTile.componentName} label="" size="medium" />`,
	},
	{
		label: 'Large (40px)',
		element: <objectTile.component label="" size="large" isBold={isBold} />,
		code: isBold
			? `<${objectTile.componentName} label="" size="large" isBold />`
			: `<${objectTile.componentName} label="" size="large" />`,
	},
	{
		label: 'XLarge (48px)',
		element: <objectTile.component label="" size="xlarge" isBold={isBold} />,
		code: isBold
			? `<${objectTile.componentName} label="" size="xlarge" isBold />`
			: `<${objectTile.componentName} label="" size="xlarge" />`,
	},
];
